import { AnimatePresence, motion, type PanInfo } from 'motion/react';
import { type CSSProperties, type ReactNode, useEffect, useId, useRef } from 'react';
import { useResolvedLayout } from '@/shared/lib/layout';

export type AdaptiveDialogSurface = 'paper' | 'plain' | 'bare';

export type AdaptiveDialogProps = {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  /** Optional descriptive text wired up via `aria-describedby`. */
  description?: string;
  /** Aria-label for the backdrop dismiss button. Defaults to `ariaLabel`. */
  closeLabel?: string;
  /** Visual classes for the panel surface; structural classes always applied. */
  panelClassName?: string;
  /** Mobile drag-handle classes; structural classes always applied. */
  handleClassName?: string;
  /** Hide the mobile drag handle. Default false. */
  hideHandle?: boolean;
  /** Disable mobile swipe-down-to-dismiss. Default false (gesture enabled). */
  disableSwipeDismiss?: boolean;
  /** Desktop centered-modal max width. Default 520. */
  desktopMaxWidth?: number | string;
  /** Visual treatment. `paper` (default) — cream paper with dot stipple,
   *  no ink border, deep paper shadow. `plain` — legacy hard-edge chrome
   *  for utility/confirm dialogs that don't suit the paper aesthetic. */
  surface?: AdaptiveDialogSurface;
  /** Desktop modal rotation in degrees. Default `-1.5`. Pass `0` to keep
   *  the modal axis-aligned (forms, confirms). Ignored on mobile. */
  tilt?: number;
  /** Render two cream tape strips at the top corners on desktop. Default
   *  `true` when `surface === 'paper'`. Ignored on mobile. */
  showTape?: boolean;
  /** Optional sticky header (title bar, close affordance). Pinned to the
   *  top of the panel above the scroll area, so the close button doesn't
   *  scroll away on tall content. Padding is applied by the primitive
   *  (`px-6 pt-6 pb-2` on modal, `px-6 pt-1 pb-2` on sheet — drag handle
   *  owns the top padding). */
  header?: ReactNode;
  /** Optional sticky footer (CTAs, secondary actions). Pinned to the
   *  bottom of the panel; the body above it scrolls when the panel hits
   *  its max height. Use this rather than placing buttons at the end of
   *  `children` when content can grow tall. */
  footer?: ReactNode;
  children: ReactNode;
};

/** Drop-onClose threshold — both magnitude and velocity must exceed. */
const SWIPE_OFFSET_PX = 120;
const SWIPE_VELOCITY_PX_S = 500;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

type PanelLook = { className: string; style: CSSProperties };

const MODAL_LOOKS: Record<AdaptiveDialogSurface, PanelLook> = {
  paper: {
    className:
      'paper-grain relative flex max-h-full flex-col overflow-hidden rounded-[6px] bg-cream',
    style: {
      boxShadow:
        'var(--border-paper), var(--shadow-paper-deep), 0 4px 0 rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.45)',
    },
  },
  plain: {
    className:
      'relative flex max-h-full flex-col overflow-hidden rounded-2xl border-2 border-ink bg-cream',
    style: { boxShadow: '6px 8px 0 rgba(0,0,0,0.35)' },
  },
  // No surface chrome — children own the visual entirely. Useful when the
  // content is its own paper/card and the dialog should be invisible.
  bare: {
    className: 'relative flex max-h-full flex-col overflow-hidden',
    style: {},
  },
};

const SHEET_LOOKS: Record<AdaptiveDialogSurface, PanelLook> = {
  paper: {
    className: 'paper-grain overflow-hidden bg-cream',
    style: {
      boxShadow:
        'var(--border-paper), 0 -14px 30px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.45)',
    },
  },
  plain: {
    className: 'overflow-hidden bg-cream',
    style: { boxShadow: '0 -10px 30px rgba(0,0,0,0.3)' },
  },
  bare: {
    className: 'overflow-hidden',
    style: {},
  },
};

const resolveLook = (look: PanelLook, override: string | undefined): PanelLook =>
  override === undefined ? look : { className: override, style: {} };

const HEADER_MODAL_CLASS = 'shrink-0 px-6 pt-6 pb-2';
const HEADER_SHEET_CLASS = 'shrink-0 px-6 pt-1 pb-2';
const FOOTER_MODAL_CLASS = 'shrink-0 border-ink/15 border-t bg-cream/95 px-6 py-4 backdrop-blur-sm';
const FOOTER_SHEET_CLASS =
  'shrink-0 border-ink/15 border-t bg-cream/95 px-6 pt-3 backdrop-blur-sm';
const SAFE_BOTTOM_PB: CSSProperties = {
  paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)',
};

const SCROLL_MODAL_CLASS = 'min-h-0 flex-1 overflow-y-auto px-6 pb-6';
const SCROLL_MODAL_PADDED_CLASS = 'min-h-0 flex-1 overflow-y-auto p-6';
const SCROLL_SHEET_CLASS = 'min-h-0 flex-1 overflow-y-auto px-6 pb-2';

const TapeStrips = () => (
  <>
    <span
      aria-hidden
      className="tape-strip"
      style={{
        top: -10,
        left: 36,
        width: 110,
        height: 18,
        transform: 'rotate(-22deg)',
        zIndex: 2,
      }}
    />
    <span
      aria-hidden
      className="tape-strip"
      style={{
        top: -10,
        right: 36,
        width: 110,
        height: 18,
        transform: 'rotate(18deg)',
        zIndex: 2,
      }}
    />
  </>
);

type ModalShellProps = {
  panelRef: React.Ref<HTMLDivElement>;
  desktopMaxWidth: number | string;
  desktopRotate: number;
  tapeOn: boolean;
  look: PanelLook;
  header: ReactNode;
  footer: ReactNode;
  children: ReactNode;
};

const ModalShell = ({
  panelRef,
  desktopMaxWidth,
  desktopRotate,
  tapeOn,
  look,
  header,
  footer,
  children,
}: ModalShellProps) => (
  <motion.div
    ref={panelRef}
    className="relative flex max-h-full w-full flex-col"
    style={{ maxWidth: desktopMaxWidth, maxHeight: 'calc(100dvh - 3rem)', transformOrigin: 'center' }}
    initial={{ scale: 0.92, opacity: 0, y: 8, rotate: 0 }}
    animate={{ scale: 1, opacity: 1, y: 0, rotate: desktopRotate }}
    exit={{ scale: 0.94, opacity: 0, y: 6, rotate: 0 }}
    transition={{ type: 'spring', damping: 24, stiffness: 280 }}
  >
    {tapeOn && <TapeStrips />}
    <div className={look.className} style={look.style}>
      {header && <div className={HEADER_MODAL_CLASS}>{header}</div>}
      <div className={header ? SCROLL_MODAL_CLASS : SCROLL_MODAL_PADDED_CLASS}>{children}</div>
      {footer && <div className={FOOTER_MODAL_CLASS}>{footer}</div>}
    </div>
  </motion.div>
);

type SheetShellProps = {
  panelRef: React.Ref<HTMLDivElement>;
  hideHandle: boolean;
  handleClass: string;
  disableSwipeDismiss: boolean;
  onDragEnd: (e: unknown, info: PanInfo) => void;
  look: PanelLook;
  bare: boolean;
  header: ReactNode;
  footer: ReactNode;
  children: ReactNode;
};

const SheetShell = ({
  panelRef,
  hideHandle,
  handleClass,
  disableSwipeDismiss,
  onDragEnd,
  look,
  bare,
  header,
  footer,
  children,
}: SheetShellProps) => (
  <motion.div
    ref={panelRef}
    className={[
      'absolute right-0 bottom-0 left-0 flex max-h-[92dvh] flex-col',
      bare ? '' : 'rounded-t-3xl pt-3',
      look.className,
    ]
      .filter(Boolean)
      .join(' ')}
    style={footer ? look.style : { ...look.style, ...SAFE_BOTTOM_PB }}
    initial={{ y: '100%' }}
    animate={{ y: 0 }}
    exit={{ y: '100%' }}
    transition={{ type: 'spring', damping: 28, stiffness: 280 }}
    drag={disableSwipeDismiss ? false : 'y'}
    dragConstraints={{ top: 0, bottom: 0 }}
    dragElastic={{ top: 0, bottom: 0.6 }}
    onDragEnd={onDragEnd}
  >
    {!hideHandle && !bare && (
      <div className={['mx-auto mb-3 h-1 w-10 shrink-0 rounded-full', handleClass].join(' ')} />
    )}
    {header && <div className={HEADER_SHEET_CLASS}>{header}</div>}
    <div className={bare ? 'min-h-0 flex-1 overflow-y-auto pt-3' : SCROLL_SHEET_CLASS}>
      {children}
    </div>
    {footer && (
      <div className={FOOTER_SHEET_CLASS} style={SAFE_BOTTOM_PB}>
        {footer}
      </div>
    )}
  </motion.div>
);

/**
 * One dialog primitive that adapts to viewport. On mobile it slides up as a
 * bottom sheet (drag handle + swipe-down dismiss); on desktop it scales in
 * as a centered modal capped at `desktopMaxWidth`. Owns Esc, backdrop
 * click, body scroll lock, focus trap + restore, and `role="dialog"
 * aria-modal`.
 *
 * Visual default is paper: cream surface with dot stipple, deep paper
 * shadow, no ink border, slight tilt on desktop, and cream tape strips
 * at the top corners. Pass `surface="plain"` to fall back to the
 * hard-edge chrome for utility/confirm dialogs.
 */
export const AdaptiveDialog = ({
  open,
  onClose,
  ariaLabel,
  description,
  closeLabel,
  panelClassName,
  handleClassName,
  hideHandle = false,
  disableSwipeDismiss = false,
  desktopMaxWidth = 520,
  surface = 'paper',
  tilt = -1.5,
  showTape,
  header,
  footer,
  children,
}: AdaptiveDialogProps) => {
  const layout = useResolvedLayout();
  // Tablet shares the centered-modal treatment with desktop. Only the
  // narrowest mobile viewport gets the bottom-sheet shape.
  const isModal = layout !== 'mobile';
  const descId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const tapeOn = (showTape ?? surface === 'paper') && surface === 'paper';
  const desktopRotate = surface === 'paper' ? tilt : 0;

  // Body scroll lock + Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Focus trap: capture last-focused element, focus first focusable in
  // panel, intercept Tab to cycle within. Restore on close.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const panel = panelRef.current;
    if (!panel) return;

    const focusables = (): HTMLElement[] =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('inert') && el.offsetParent !== null,
      );

    const first = focusables()[0];
    first?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) return;
      const firstEl = list[0]!;
      const lastEl = list[list.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    panel.addEventListener('keydown', onKey);
    return () => {
      panel.removeEventListener('keydown', onKey);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  const onSheetDragEnd = (_e: unknown, info: PanInfo): void => {
    if (disableSwipeDismiss) return;
    if (info.offset.y > SWIPE_OFFSET_PX || info.velocity.y > SWIPE_VELOCITY_PX_S) onClose();
  };

  const modalLook = resolveLook(MODAL_LOOKS[surface], panelClassName);
  const sheetLook = resolveLook(SHEET_LOOKS[surface], panelClassName);
  const sheetHandleClass = handleClassName ?? (surface === 'paper' ? 'bg-pencil/30' : 'bg-ink/20');

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={['fixed inset-0 z-50', isModal ? 'flex items-center justify-center p-6' : '']
            .filter(Boolean)
            .join(' ')}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-describedby={description ? descId : undefined}
        >
          {description && (
            <p id={descId} className="sr-only">
              {description}
            </p>
          )}
          <motion.button
            type="button"
            aria-label={closeLabel ?? ariaLabel}
            onClick={onClose}
            className="absolute inset-0 h-full w-full bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          />

          {isModal ? (
            <ModalShell
              panelRef={panelRef}
              desktopMaxWidth={desktopMaxWidth}
              desktopRotate={desktopRotate}
              tapeOn={tapeOn}
              look={modalLook}
              header={header}
              footer={footer}
            >
              {children}
            </ModalShell>
          ) : (
            <SheetShell
              panelRef={panelRef}
              hideHandle={hideHandle}
              handleClass={sheetHandleClass}
              disableSwipeDismiss={disableSwipeDismiss}
              onDragEnd={onSheetDragEnd}
              look={sheetLook}
              bare={surface === 'bare'}
              header={header}
              footer={footer}
            >
              {children}
            </SheetShell>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
